import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Zap, Users, Trophy, Sparkles, Star, Quote, Calendar, 
  Flame, Target, Clock, ChevronRight, ChevronLeft, Award, BarChart3,
  Play, Crown, Medal, TrendingUp, Brain, Lightbulb, PieChart, LayoutGrid, Layers, Leaf,
  HelpCircle, Search, Eye, EyeOff, BookOpen
} from 'lucide-react';
import api, { publicAPI } from '../lib/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

// Feature screenshots - using placeholder gradients for visual appeal
const FEATURE_IMAGES = {
  dailyChallenge: "https://customer-assets.emergentagent.com/job_e2e25c87-ca95-4bbc-a856-cdbde9df0423/artifacts/dashboard_screenshot.png",
  leaderboard: "https://customer-assets.emergentagent.com/job_e2e25c87-ca95-4bbc-a856-cdbde9df0423/artifacts/leaderboard_screenshot.png",
  sprint: "https://customer-assets.emergentagent.com/job_e2e25c87-ca95-4bbc-a856-cdbde9df0423/artifacts/sprint_screenshot.png"
};

// Testimonials data
const testimonials = [
  {
    name: "Sarah M.",
    title: "Trivia Enthusiast",
    quote: "The AI-generated boards are incredible — 3 full rounds with escalating values just like the real show. I create a new board every night and my category knowledge has skyrocketed.",
    rating: 5
  },
  {
    name: "Mike T.",
    title: "Tournament Competitor",
    quote: "Sprint mode completely changed my training. The 20-second timer and detailed category analysis helped me find my weak spots. My Daily Challenge scores have doubled since I started.",
    rating: 5
  },
  {
    name: "Jennifer L.",
    title: "Data-Driven Player",
    quote: "AskAlex is a game-changer. Beating real contestant averages in Beat the Data and tracking my progress in the Knowledge Portfolio keeps me coming back every day.",
    rating: 5
  }
];


const FAQ_DATA = [
  {
    q: 'How do I practice for Jeopardy?',
    a: 'JepPrep offers multiple practice modes: Daily Challenge with 10 fresh clues, Sprint Mode with timed 15-50 question sessions, Daily Categories for focused topic practice, and AI-generated Boards with full 3-round Jeopardy-style gameplay. Track your progress with detailed analytics.'
  },
  {
    q: 'Where can I find Jeopardy test questions?',
    a: 'JepPrep has a database of over 514,000 Jeopardy-style practice questions across 54,000+ categories. New questions are generated daily using AI, and every clue is paraphrased for originality. Use the Clue Explorer to search and filter the entire database.'
  },
  {
    q: 'What are the hardest Jeopardy categories?',
    a: 'Based on our data from 514K+ clues, historically challenging categories include Opera, Classical Music, World Geography, Shakespeare, and Ancient History. JepPrep tracks your accuracy by category so you can identify and improve on your weakest areas.'
  },
  {
    q: 'How many questions are on Jeopardy?',
    a: 'A standard Jeopardy game has 61 clues: 30 in the first round, 30 in Double Jeopardy, and 1 Final Jeopardy question. JepPrep boards replicate this with AI-generated clues across 6 categories, escalating values, and a Final Round wager.'
  },
  {
    q: 'Is JepPrep free to use?',
    a: 'JepPrep offers a free trial with full access to all features including Daily Challenges, Sprint Mode, Daily Categories, AI Board Generation, Leaderboards, and the AskAlex analytics suite. Premium plans start at $14/month.'
  },
  {
    q: 'What is the best way to study for Jeopardy?',
    a: 'Consistent daily practice across diverse categories is key. Use JepPrep\'s Daily Challenge to build streaks, Sprint Mode for speed training, and AskAlex\'s Clue Explorer to identify knowledge gaps. The platform tracks your accuracy across 54,000+ categories so you know exactly where to focus.'
  }
];

function SampleQuestionsSection() {
  const [questions, setQuestions] = useState([]);
  const [revealed, setRevealed] = useState({});

  useEffect(() => {
    api.get('/api/public/sample-questions')
      .then(res => setQuestions(res.data || []))
      .catch(() => {});
  }, []);

  if (questions.length === 0) return null;

  return (
    <div className="py-20 bg-[hsl(var(--secondary))]" data-testid="sample-questions-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-display font-bold text-3xl text-white mb-4">
            Sample Jeopardy <span className="text-[hsl(var(--brand-gold-500))]">Practice Questions</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Try these real questions from our database of 514,000+ Jeopardy-style clues. 
            Click to reveal each answer.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {questions.map((q, idx) => (
            <div
              key={idx}
              className="bg-[hsl(var(--brand-blue-800))] border border-[hsl(var(--border))] rounded-lg p-4 flex flex-col"
              data-testid={`sample-question-${idx}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-[hsl(var(--brand-gold-500))] text-xs font-semibold truncate max-w-[70%]">{q.category}</span>
                {q.value && <span className="text-emerald-400 text-xs font-medium">${q.value}</span>}
              </div>
              <p className="text-gray-200 text-sm flex-1 mb-3">{q.question}</p>
              <button
                onClick={() => setRevealed(prev => ({ ...prev, [idx]: !prev[idx] }))}
                className="flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors mt-auto"
              >
                {revealed[idx] ? (
                  <><EyeOff className="w-3 h-3" /> {q.answer}</>
                ) : (
                  <><Eye className="w-3 h-3" /> Reveal Answer</>
                )}
              </button>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/register">
            <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white font-semibold">
              <Search className="w-4 h-4 mr-2" />
              Explore All 514,000+ Questions
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);
  
  return (
    <div className="py-20 bg-[hsl(var(--background))]" data-testid="faq-section">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-display font-bold text-3xl text-white mb-4">
            Frequently Asked <span className="text-[hsl(var(--brand-gold-500))]">Questions</span>
          </h2>
          <p className="text-gray-400">
            Everything you need to know about practicing Jeopardy test questions with JepPrep
          </p>
        </div>
        <div className="space-y-3">
          {FAQ_DATA.map((faq, idx) => (
            <div
              key={idx}
              className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-lg overflow-hidden"
              data-testid={`faq-item-${idx}`}
            >
              <button
                onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
                className="w-full flex items-center justify-between p-4 text-left"
              >
                <span className="text-white text-sm font-medium pr-4">{faq.q}</span>
                <HelpCircle className={`w-5 h-5 shrink-0 transition-colors ${openIdx === idx ? 'text-[hsl(var(--brand-gold-500))]' : 'text-gray-500'}`} />
              </button>
              {openIdx === idx && (
                <div className="px-4 pb-4">
                  <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function BlogCarousel() {
  const [posts, setPosts] = useState([]);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    api.get('/api/blog/posts').then(res => {
      setPosts(res.data?.posts || []);
    }).catch(() => {});
  }, []);

  if (posts.length === 0) return null;

  // Desktop shows 3, mobile shows 1
  const visibleDesktop = 3;
  const maxOffset = Math.max(0, posts.length - visibleDesktop);

  const goPrev = () => setOffset(o => Math.max(0, o - 1));
  const goNext = () => setOffset(o => Math.min(maxOffset, o + 1));

  return (
    <div className="py-20 bg-[hsl(var(--secondary))]" data-testid="blog-carousel-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-display font-bold text-3xl text-white mb-4">
            From the <span className="text-[hsl(var(--brand-gold-500))]">JepPrep Blog</span>
          </h2>
          <p className="text-gray-400">
            Expert strategies, study guides, and the science behind winning at trivia
          </p>
        </div>

        <div className="relative">
          {/* Desktop prev arrow */}
          {offset > 0 && (
            <button
              onClick={goPrev}
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[hsl(var(--background))] border border-[hsl(var(--border))] flex items-center justify-center text-gray-400 hover:text-white hover:border-[hsl(var(--brand-gold-500))] transition-all hidden sm:flex"
              data-testid="blog-carousel-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}

          {/* Cards container */}
          <div className="overflow-hidden">
            {/* Mobile: horizontal scroll */}
            <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 sm:hidden no-scrollbar">
              {posts.map((post) => (
                <Link
                  key={post.id || post.slug}
                  to={`/blog/${post.slug}`}
                  className="w-[85%] flex-shrink-0 snap-center group"
                >
                  <Card className="bg-[hsl(var(--background))] border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 transition-all h-full">
                    <CardContent className="p-5 flex flex-col h-full">
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(post.tags || []).slice(0, 3).map(tag => (
                          <Badge key={tag} className="bg-[hsl(var(--brand-gold-500))]/10 text-[hsl(var(--brand-gold-500))] border-[hsl(var(--brand-gold-500))]/20 text-xs">{tag}</Badge>
                        ))}
                      </div>
                      <h3 className="text-white font-bold text-base mb-2 group-hover:text-[hsl(var(--brand-gold-500))] transition-colors line-clamp-2">{post.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">{post.excerpt || post.meta_description}</p>
                      <span className="text-[hsl(var(--brand-gold-500))] text-sm font-medium flex items-center gap-1">Read article <ChevronRight className="w-4 h-4" /></span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Desktop: sliding carousel */}
            <div
              className="hidden sm:flex transition-transform duration-300 ease-in-out gap-6"
              style={{
                transform: `translateX(calc(-${offset} * (100% / 3 + 8px)))`,
              }}
            >
              {posts.map((post) => (
                <Link
                  key={post.id || post.slug}
                  to={`/blog/${post.slug}`}
                  className="w-[calc(33.333%-16px)] flex-shrink-0 group"
                >
                  <Card className="bg-[hsl(var(--background))] border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/50 transition-all h-full">
                    <CardContent className="p-6 flex flex-col h-full">
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(post.tags || []).slice(0, 3).map(tag => (
                          <Badge key={tag} className="bg-[hsl(var(--brand-gold-500))]/10 text-[hsl(var(--brand-gold-500))] border-[hsl(var(--brand-gold-500))]/20 text-xs">{tag}</Badge>
                        ))}
                      </div>
                      <h3 className="text-white font-bold text-lg mb-2 group-hover:text-[hsl(var(--brand-gold-500))] transition-colors line-clamp-2">{post.title}</h3>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">{post.excerpt || post.meta_description}</p>
                      <span className="text-[hsl(var(--brand-gold-500))] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">Read article <ChevronRight className="w-4 h-4" /></span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Desktop next arrow */}
          {offset < maxOffset && (
            <button
              onClick={goNext}
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[hsl(var(--background))] border border-[hsl(var(--border))] flex items-center justify-center text-gray-400 hover:text-white hover:border-[hsl(var(--brand-gold-500))] transition-all hidden sm:flex"
              data-testid="blog-carousel-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Desktop dots */}
        <div className="hidden sm:flex justify-center gap-2 mt-8">
          {Array.from({ length: maxOffset + 1 }, (_, idx) => (
            <button
              key={idx}
              onClick={() => setOffset(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === offset ? 'bg-[hsl(var(--brand-gold-500))]' : 'bg-gray-600'
              }`}
            />
          ))}
        </div>

        {/* View all link */}
        <div className="text-center mt-8">
          <Link to="/blog">
            <Button variant="outline" className="border-[hsl(var(--border))] text-white hover:border-[hsl(var(--brand-gold-500))]" data-testid="blog-carousel-view-all">
              <BookOpen className="w-4 h-4 mr-2" />
              View All Articles
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}


export default function Landing() {
  useSEO('JepPrep - Jeopardy Practice Test Training and Questions', 'Sharpen your trivia skills with JepPrep. Practice Jeopardy-style questions, improve recall, and train like a game show contestant with interactive quizzes.', { canonical: 'https://jepprep.com/' });
  const [topPlayers, setTopPlayers] = useState([
    { display_name: 'Player 1', total_score: 1000 },
    { display_name: 'Player 2', total_score: 800 },
    { display_name: 'Player 3', total_score: 600 }
  ]);

  useEffect(() => {
    // Fetch real leaderboard data (public endpoint - no auth required)
    const fetchLeaderboard = async () => {
      try {
        const response = await publicAPI.getTop3Leaderboard();
        const top3Data = response.data?.top3 || [];
        if (top3Data.length >= 3) {
          setTopPlayers(top3Data);
        }
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      }
    };
    fetchLeaderboard();
  }, []);

  // Format score for display
  const formatScore = (score) => {
    if (!score) return '0';
    if (score >= 1000) return `${(score / 1000).toFixed(1)}k`;
    return score.toString();
  };

  const renderStars = (count) => (
    <div className="flex gap-1">
      {[...Array(count)].map((_, i) => (
        <Star key={`star-${i}`} className="w-4 h-4 fill-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-gold-500))]" />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="hero-gradient">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-2 pb-6">
          {/* Navigation */}
          <nav className="flex items-center justify-end mb-1">
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:text-[hsl(var(--brand-gold-400))]" data-testid="nav-login">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white" data-testid="nav-signup">
                  Get Started
                </Button>
              </Link>
            </div>
          </nav>

          {/* Hero Content */}
          <div className="text-center py-0">
            {/* Editor's Choice Badge */}
            <div className="flex items-center justify-center mb-2 mt-[30px]">
              <svg className="w-40 h-20" viewBox="0 0 240 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Left branch - outer leaves */}
                <g fill="hsl(45, 100%, 51%)" opacity="0.9">
                  <ellipse cx="42" cy="6" rx="2.5" ry="6" transform="rotate(-55 42 6)" />
                  <ellipse cx="34" cy="12" rx="2.5" ry="6" transform="rotate(-45 34 12)" />
                  <ellipse cx="27" cy="20" rx="2.5" ry="6" transform="rotate(-35 27 20)" />
                  <ellipse cx="22" cy="29" rx="2.5" ry="6" transform="rotate(-20 22 29)" />
                  <ellipse cx="18" cy="39" rx="2.5" ry="6" transform="rotate(-8 18 39)" />
                  <ellipse cx="17" cy="49" rx="2.5" ry="6" transform="rotate(5 17 49)" />
                  <ellipse cx="19" cy="59" rx="2.5" ry="6" transform="rotate(18 19 59)" />
                  <ellipse cx="23" cy="68" rx="2.5" ry="6" transform="rotate(30 23 68)" />
                  <ellipse cx="30" cy="76" rx="2.5" ry="6" transform="rotate(42 30 76)" />
                  <ellipse cx="38" cy="82" rx="2.5" ry="6" transform="rotate(55 38 82)" />
                  <ellipse cx="48" cy="87" rx="2.5" ry="6" transform="rotate(65 48 87)" />
                </g>
                {/* Left branch - inner leaves */}
                <g fill="hsl(45, 100%, 51%)" opacity="0.9">
                  <ellipse cx="50" cy="11" rx="2" ry="5" transform="rotate(-70 50 11)" />
                  <ellipse cx="43" cy="17" rx="2" ry="5" transform="rotate(-58 43 17)" />
                  <ellipse cx="37" cy="25" rx="2" ry="5" transform="rotate(-45 37 25)" />
                  <ellipse cx="32" cy="33" rx="2" ry="5" transform="rotate(-30 32 33)" />
                  <ellipse cx="28" cy="43" rx="2" ry="5" transform="rotate(-15 28 43)" />
                  <ellipse cx="27" cy="53" rx="2" ry="5" transform="rotate(0 27 53)" />
                  <ellipse cx="29" cy="62" rx="2" ry="5" transform="rotate(15 29 62)" />
                  <ellipse cx="34" cy="71" rx="2" ry="5" transform="rotate(28 34 71)" />
                  <ellipse cx="41" cy="78" rx="2" ry="5" transform="rotate(42 41 78)" />
                  <ellipse cx="50" cy="84" rx="2" ry="5" transform="rotate(55 50 84)" />
                </g>
                {/* Left stem */}
                <path d="M52 4 Q14 40 55 92" stroke="hsl(45, 100%, 51%)" strokeWidth="1.5" fill="none" opacity="0.7" />

                {/* Right branch - outer leaves */}
                <g fill="hsl(45, 100%, 51%)" opacity="0.9">
                  <ellipse cx="198" cy="6" rx="2.5" ry="6" transform="rotate(55 198 6)" />
                  <ellipse cx="206" cy="12" rx="2.5" ry="6" transform="rotate(45 206 12)" />
                  <ellipse cx="213" cy="20" rx="2.5" ry="6" transform="rotate(35 213 20)" />
                  <ellipse cx="218" cy="29" rx="2.5" ry="6" transform="rotate(20 218 29)" />
                  <ellipse cx="222" cy="39" rx="2.5" ry="6" transform="rotate(8 222 39)" />
                  <ellipse cx="223" cy="49" rx="2.5" ry="6" transform="rotate(-5 223 49)" />
                  <ellipse cx="221" cy="59" rx="2.5" ry="6" transform="rotate(-18 221 59)" />
                  <ellipse cx="217" cy="68" rx="2.5" ry="6" transform="rotate(-30 217 68)" />
                  <ellipse cx="210" cy="76" rx="2.5" ry="6" transform="rotate(-42 210 76)" />
                  <ellipse cx="202" cy="82" rx="2.5" ry="6" transform="rotate(-55 202 82)" />
                  <ellipse cx="192" cy="87" rx="2.5" ry="6" transform="rotate(-65 192 87)" />
                </g>
                {/* Right branch - inner leaves */}
                <g fill="hsl(45, 100%, 51%)" opacity="0.9">
                  <ellipse cx="190" cy="11" rx="2" ry="5" transform="rotate(70 190 11)" />
                  <ellipse cx="197" cy="17" rx="2" ry="5" transform="rotate(58 197 17)" />
                  <ellipse cx="203" cy="25" rx="2" ry="5" transform="rotate(45 203 25)" />
                  <ellipse cx="208" cy="33" rx="2" ry="5" transform="rotate(30 208 33)" />
                  <ellipse cx="212" cy="43" rx="2" ry="5" transform="rotate(15 212 43)" />
                  <ellipse cx="213" cy="53" rx="2" ry="5" transform="rotate(0 213 53)" />
                  <ellipse cx="211" cy="62" rx="2" ry="5" transform="rotate(-15 211 62)" />
                  <ellipse cx="206" cy="71" rx="2" ry="5" transform="rotate(-28 206 71)" />
                  <ellipse cx="199" cy="78" rx="2" ry="5" transform="rotate(-42 199 78)" />
                  <ellipse cx="190" cy="84" rx="2" ry="5" transform="rotate(-55 190 84)" />
                </g>
                {/* Right stem */}
                <path d="M188 4 Q226 40 185 92" stroke="hsl(45, 100%, 51%)" strokeWidth="1.5" fill="none" opacity="0.7" />

                {/* Text */}
                <text x="120" y="44" textAnchor="middle" fill="hsl(45, 100%, 51%)" fontFamily="system-ui, sans-serif" fontSize="15" fontWeight="700" letterSpacing="3">EDITOR'S</text>
                <text x="120" y="63" textAnchor="middle" fill="hsl(45, 100%, 51%)" fontFamily="system-ui, sans-serif" fontSize="15" fontWeight="700" letterSpacing="3">CHOICE</text>
              </svg>
            </div>
            <img src={LOGO_URL} alt="Jeopardy practice" className="h-48 md:h-64 lg:h-72 mx-auto mb-2" />
            
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-3" data-testid="hero-title">
              Practice Jeopardy Test Questions<br />
              <span className="text-[hsl(var(--brand-gold-500))]">Train Like a Champion</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto mb-4">
              AI-powered trivia training with 514,000+ Jeopardy-style practice questions 
              across 54,000 categories. Create boards, compete globally, and track your progress.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="w-full sm:w-auto bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 py-6 text-lg font-semibold" data-testid="hero-cta-primary">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Features Grid */}
      <div className="py-16 bg-[hsl(var(--background))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-3xl text-center text-white mb-12">
            514,000+ Jeopardy Practice Questions to <span className="text-[hsl(var(--brand-gold-500))]">Dominate</span>
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-xl p-6 card-hover">
              <div className="w-12 h-12 bg-[hsl(var(--brand-blue-800))] rounded-lg flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-[hsl(var(--brand-gold-500))]" />
              </div>
              <h3 className="font-semibold text-lg text-white mb-2">Daily Challenge</h3>
              <p className="text-gray-400 text-sm">
                10 fresh clues every day across 5 categories. Compete globally and climb the leaderboard.
              </p>
            </div>

            <div className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-xl p-6 card-hover">
              <div className="w-12 h-12 bg-[hsl(var(--brand-blue-800))] rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-lg text-white mb-2">Sprint Mode</h3>
              <p className="text-gray-400 text-sm">
                Solo rapid-fire practice. 15, 25, or 50 questions with a 20-second timer and detailed analysis.
              </p>
            </div>

            <div className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-xl p-6 card-hover">
              <div className="w-12 h-12 bg-[hsl(var(--brand-blue-800))] rounded-lg flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-violet-400" />
              </div>
              <h3 className="font-semibold text-lg text-white mb-2">Daily Categories</h3>
              <p className="text-gray-400 text-sm">
                3 featured categories daily. Choose your difficulty and clue count for flexible training.
              </p>
            </div>

            <div className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] rounded-xl p-6 card-hover">
              <div className="w-12 h-12 bg-[hsl(var(--brand-blue-800))] rounded-lg flex items-center justify-center mb-4">
                <Brain className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="font-semibold text-lg text-white mb-2">AskAlex</h3>
              <p className="text-gray-400 text-sm">
                Data-driven missions, Clue Explorer search across 514K+ clues, and a knowledge portfolio.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== DAILY CHALLENGE SHOWCASE ==================== */}
      <div className="py-20 bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <Badge className="bg-[hsl(var(--brand-gold-500))]/20 text-[hsl(var(--brand-gold-500))] border-[hsl(var(--brand-gold-500))]/30 mb-4">
                <Calendar className="w-3 h-3 mr-1" />
                Daily Challenge
              </Badge>
              <h2 className="font-display font-bold text-3xl lg:text-4xl text-white mb-4">
                Compete Daily.<br />
                <span className="text-[hsl(var(--brand-gold-500))]">Climb the Leaderboard.</span>
              </h2>
              <p className="text-gray-400 text-lg mb-6">
                Every day brings a new challenge with 10 fresh clues across 5 categories. 
                Race against time and compete with players worldwide for the top spot.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center">
                    <Flame className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Build Your Streak</h4>
                    <p className="text-gray-500 text-sm">Play daily to maintain and grow your streak</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Global Leaderboards</h4>
                    <p className="text-gray-500 text-sm">Today, This Week, and All-Time rankings</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[hsl(var(--brand-gold-500))]/20 flex items-center justify-center">
                    <Award className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Earn Achievements</h4>
                    <p className="text-gray-500 text-sm">Unlock badges and showcase your skills</p>
                  </div>
                </div>
              </div>
              
              <Link to="/register">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                  Start Today's Challenge
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            {/* Right: Screenshot Mockup */}
            <div className="relative">
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-2xl p-6 border border-[hsl(var(--border))] shadow-2xl">
                {/* Mock Daily Challenge Card */}
                <div className="bg-[hsl(var(--brand-blue-900))] rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-[hsl(var(--brand-gold-500))] flex items-center justify-center">
                      <Zap className="w-8 h-8 text-[hsl(var(--brand-blue-900))]" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white text-center mb-2">Ready to Play?</h3>
                  <p className="text-gray-400 text-sm text-center mb-4">Answer 10 clues across 5 categories</p>
                  <div className="flex flex-wrap justify-center gap-2 mb-4">
                    {['HISTORY', 'SCIENCE', 'SPORTS', 'ARTS', 'GEOGRAPHY'].map((cat) => (
                      <Badge key={cat} className="bg-[hsl(var(--secondary))] text-gray-300 text-xs">{cat}</Badge>
                    ))}
                  </div>
                  <Button className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                    <Play className="w-4 h-4 mr-2" />
                    Start Challenge
                  </Button>
                </div>
                
                {/* Stats Row */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-[hsl(var(--brand-blue-900))] rounded-lg p-3 text-center">
                    <Flame className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                    <p className="text-white font-bold">7</p>
                    <p className="text-gray-500 text-xs">Day Streak</p>
                  </div>
                  <div className="bg-[hsl(var(--brand-blue-900))] rounded-lg p-3 text-center">
                    <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold-500))] mx-auto mb-1" />
                    <p className="text-white font-bold">#12</p>
                    <p className="text-gray-500 text-xs">Rank</p>
                  </div>
                  <div className="bg-[hsl(var(--brand-blue-900))] rounded-lg p-3 text-center">
                    <Users className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                    <p className="text-white font-bold">248</p>
                    <p className="text-gray-500 text-xs">Players</p>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[hsl(var(--brand-gold-500))]/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-[hsl(var(--brand-blue-800))]/50 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* ==================== LEADERBOARD SHOWCASE ==================== */}
      <div className="py-20 bg-[hsl(var(--background))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Screenshot Mockup */}
            <div className="relative order-2 lg:order-1">
              <div className="bg-[hsl(var(--secondary))] rounded-2xl p-6 border border-[hsl(var(--border))] shadow-2xl">
                {/* Period Tabs */}
                <div className="flex justify-center mb-6">
                  <div className="inline-flex bg-[hsl(var(--brand-blue-800))] rounded-lg p-1">
                    <span className="px-4 py-2 text-gray-400 text-sm">Today</span>
                    <span className="px-4 py-2 text-gray-400 text-sm">This Week</span>
                    <span className="px-4 py-2 bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] rounded-md text-sm font-medium">All Time</span>
                  </div>
                </div>
                
                {/* Podium - Dynamic Top 3 */}
                <div className="flex justify-center items-end gap-4 mb-6">
                  {/* 2nd Place */}
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 p-0.5 mx-auto mb-2">
                      <div className="w-full h-full rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center">
                        <span className="text-gray-300 font-bold">{topPlayers[1]?.display_name?.charAt(0) || 'L'}</span>
                      </div>
                    </div>
                    <Medal className="w-5 h-5 text-gray-300 mx-auto mb-1" />
                    <p className="text-white text-sm truncate max-w-[80px]">{topPlayers[1]?.display_name || 'Player 2'}</p>
                    <p className="text-[hsl(var(--brand-gold-500))] font-bold">{formatScore(topPlayers[1]?.total_score)}</p>
                    <div className="w-16 h-20 bg-gradient-to-t from-gray-400/20 to-transparent rounded-t-lg mt-2 mx-auto" />
                  </div>
                  
                  {/* 1st Place */}
                  <div className="text-center -mt-4">
                    <Crown className="w-6 h-6 text-yellow-400 mx-auto mb-1" />
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 p-0.5 mx-auto mb-2">
                      <div className="w-full h-full rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center">
                        <span className="text-yellow-400 font-bold">{topPlayers[0]?.display_name?.charAt(0) || 'M'}</span>
                      </div>
                    </div>
                    <p className="text-white font-bold truncate max-w-[100px]">{topPlayers[0]?.display_name || 'Champion'}</p>
                    <p className="text-[hsl(var(--brand-gold-500))] font-bold text-lg">{formatScore(topPlayers[0]?.total_score)}</p>
                    <div className="w-20 h-28 bg-gradient-to-t from-yellow-400/20 to-transparent rounded-t-lg mt-2 mx-auto" />
                  </div>
                  
                  {/* 3rd Place */}
                  <div className="text-center">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 p-0.5 mx-auto mb-2">
                      <div className="w-full h-full rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center">
                        <span className="text-orange-400 font-bold">{topPlayers[2]?.display_name?.charAt(0) || 'R'}</span>
                      </div>
                    </div>
                    <Award className="w-5 h-5 text-orange-400 mx-auto mb-1" />
                    <p className="text-white text-sm truncate max-w-[80px]">{topPlayers[2]?.display_name || 'Player 3'}</p>
                    <p className="text-[hsl(var(--brand-gold-500))] font-bold">{formatScore(topPlayers[2]?.total_score)}</p>
                    <div className="w-16 h-14 bg-gradient-to-t from-orange-400/20 to-transparent rounded-t-lg mt-2 mx-auto" />
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-yellow-500/10 rounded-full blur-2xl" />
            </div>
            
            {/* Right: Content */}
            <div className="order-1 lg:order-2">
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 mb-4">
                <Trophy className="w-3 h-3 mr-1" />
                Leaderboards
              </Badge>
              <h2 className="font-display font-bold text-3xl lg:text-4xl text-white mb-4">
                See Where You<br />
                <span className="text-[hsl(var(--brand-gold-500))]">Stand Against the Best</span>
              </h2>
              <p className="text-gray-400 text-lg mb-6">
                Track your progress with detailed leaderboards. Compare your performance 
                across different time periods and see how you stack up against other players.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Podium Display</h4>
                    <p className="text-gray-500 text-sm">Top 3 players showcased with medals</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Multiple Time Periods</h4>
                    <p className="text-gray-500 text-sm">Today, This Week, and All-Time views</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Track Your Rank</h4>
                    <p className="text-gray-500 text-sm">See your position and nearby competitors</p>
                  </div>
                </div>
              </div>
              
              <Link to="/register">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                  Join the Competition
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== SPRINT MODE SHOWCASE ==================== */}
      <div className="py-20 bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 mb-4">
                <Zap className="w-3 h-3 mr-1" />
                Sprint Mode
              </Badge>
              <h2 className="font-display font-bold text-3xl lg:text-4xl text-white mb-4">
                Rapid-Fire Training.<br />
                <span className="text-[hsl(var(--brand-gold-500))]">Master Every Category.</span>
              </h2>
              <p className="text-gray-400 text-lg mb-6">
                Push your limits with timed solo practice sessions. Choose 15, 25, or 50 questions 
                and race against a 20-second timer for each clue.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">20-Second Timer</h4>
                    <p className="text-gray-500 text-sm">Quick thinking under pressure</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Category Analysis</h4>
                    <p className="text-gray-500 text-sm">Detailed breakdown of your performance</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Identify Weaknesses</h4>
                    <p className="text-gray-500 text-sm">Focus on areas that need improvement</p>
                  </div>
                </div>
              </div>
              
              <Link to="/register">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                  Start Sprint Training
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            {/* Right: Screenshot Mockups */}
            <div className="relative space-y-6">
              {/* Sprint Mode Card */}
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-2xl p-6 border border-[hsl(var(--border))] shadow-2xl">
                <div className="bg-[hsl(var(--brand-blue-900))] rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                        <Zap className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold">Sprint Mode</h3>
                        <p className="text-gray-400 text-sm">50 questions • 20s each</p>
                      </div>
                    </div>
                    <Badge className="bg-cyan-500/20 text-cyan-400">In Progress</Badge>
                  </div>
                  
                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">Progress</span>
                      <span className="text-white">32/50</span>
                    </div>
                    <div className="h-2 bg-[hsl(var(--secondary))] rounded-full overflow-hidden">
                      <div className="h-full w-[64%] bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full" />
                    </div>
                  </div>
                  
                  {/* Current Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="text-center p-2 bg-[hsl(var(--secondary))] rounded-lg">
                      <p className="text-green-400 font-bold text-lg">24</p>
                      <p className="text-gray-500 text-xs">Correct</p>
                    </div>
                    <div className="text-center p-2 bg-[hsl(var(--secondary))] rounded-lg">
                      <p className="text-red-400 font-bold text-lg">8</p>
                      <p className="text-gray-500 text-xs">Incorrect</p>
                    </div>
                    <div className="text-center p-2 bg-[hsl(var(--secondary))] rounded-lg">
                      <p className="text-white font-bold text-lg">75%</p>
                      <p className="text-gray-500 text-xs">Accuracy</p>
                    </div>
                  </div>
                </div>
                
                {/* Category Performance Preview */}
                <div className="bg-[hsl(var(--brand-blue-900))] rounded-xl p-4">
                  <h4 className="text-white font-medium mb-3 text-sm">Category Performance</h4>
                  <div className="space-y-2">
                    {[
                      { name: 'History', pct: 85, color: 'bg-green-500' },
                      { name: 'Science', pct: 70, color: 'bg-yellow-500' },
                      { name: 'Sports', pct: 60, color: 'bg-orange-500' },
                    ].map((cat) => (
                      <div key={cat.name} className="flex items-center gap-3">
                        <span className="text-gray-400 text-xs w-16">{cat.name}</span>
                        <div className="flex-1 h-2 bg-[hsl(var(--secondary))] rounded-full overflow-hidden">
                          <div className={`h-full ${cat.color} rounded-full`} style={{ width: `${cat.pct}%` }} />
                        </div>
                        <span className="text-white text-xs w-8">{cat.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Analytics Deep Dive Card */}
              <div className="bg-[hsl(var(--secondary))] rounded-2xl p-5 border border-[hsl(var(--border))] shadow-xl">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  <h4 className="text-white font-bold">Analytics Deep Dive</h4>
                </div>
                
                {/* Tab Preview */}
                <div className="flex gap-2 mb-4">
                  <span className="px-3 py-1 bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] rounded-md text-xs font-medium">Overview</span>
                  <span className="px-3 py-1 bg-[hsl(var(--brand-blue-800))] text-gray-400 rounded-md text-xs">Categories</span>
                  <span className="px-3 py-1 bg-[hsl(var(--brand-blue-800))] text-gray-400 rounded-md text-xs">Achievements</span>
                  <span className="px-3 py-1 bg-[hsl(var(--brand-blue-800))] text-gray-400 rounded-md text-xs">Review</span>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 text-center">
                    <Target className="w-5 h-5 mx-auto mb-1 text-[hsl(var(--brand-gold-500))]" />
                    <p className="text-white font-bold">47</p>
                    <p className="text-gray-500 text-xs">Sessions</p>
                  </div>
                  <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 text-center">
                    <Trophy className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
                    <p className="text-emerald-400 font-bold">72%</p>
                    <p className="text-gray-500 text-xs">Accuracy</p>
                  </div>
                  <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 text-center">
                    <Flame className="w-5 h-5 mx-auto mb-1 text-orange-400" />
                    <p className="text-white font-bold">12</p>
                    <p className="text-gray-500 text-xs">Day Streak</p>
                  </div>
                  <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3 text-center">
                    <Clock className="w-5 h-5 mx-auto mb-1 text-cyan-400" />
                    <p className="text-white font-bold">8.2h</p>
                    <p className="text-gray-500 text-xs">Played</p>
                  </div>
                </div>
                
                {/* Performance Trend */}
                <div className="bg-[hsl(var(--brand-blue-800))] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-xs">Recent Performance</span>
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="space-y-2">
                    {[
                      { date: 'Apr 8', pct: 78 },
                      { date: 'Apr 7', pct: 72 },
                      { date: 'Apr 6', pct: 68 },
                      { date: 'Apr 5', pct: 75 },
                    ].map((session) => (
                      <div key={session.date} className="flex items-center gap-2">
                        <span className="text-gray-500 text-xs w-12">{session.date}</span>
                        <div className="flex-1 h-1.5 bg-[hsl(var(--secondary))] rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full" 
                            style={{ 
                              width: `${session.pct}%`,
                              backgroundColor: session.pct >= 70 ? '#34d399' : session.pct >= 50 ? '#fbbf24' : '#fb7185'
                            }} 
                          />
                        </div>
                        <span className="text-emerald-400 text-xs w-8 text-right">{session.pct}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-cyan-500/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* ==================== DAILY CATEGORIES SHOWCASE ==================== */}
      <div className="py-20 bg-[hsl(var(--background))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Mockup */}
            <div className="relative space-y-6 order-2 lg:order-1">
              {/* Daily Categories Card */}
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-2xl p-6 border border-[hsl(var(--border))] shadow-2xl">
                <div className="bg-[hsl(var(--brand-blue-900))] rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-violet-400" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold">Today's Categories</h3>
                        <p className="text-gray-400 text-sm">3 new categories daily</p>
                      </div>
                    </div>
                    <Badge className="bg-violet-500/20 text-violet-400">Featured</Badge>
                  </div>
                  
                  {/* Category Cards */}
                  <div className="space-y-3">
                    {[
                      { name: 'WORLD HISTORY', plays: 12, best: '85%' },
                      { name: 'SCIENCE & NATURE', plays: 8, best: '72%' },
                      { name: 'POP CULTURE', plays: 5, best: '90%' },
                    ].map((cat, idx) => (
                      <div key={cat.name} className="flex items-center justify-between p-3 bg-[hsl(var(--secondary))] rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge className="bg-violet-500/20 text-violet-400 text-xs">#{idx + 1}</Badge>
                          <span className="text-white font-medium text-sm">{cat.name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-gray-400">{cat.plays} plays</span>
                          <span className="text-emerald-400 font-medium">{cat.best}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Training Config Preview */}
                <div className="bg-[hsl(var(--brand-blue-900))] rounded-xl p-4">
                  <h4 className="text-white font-medium mb-3 text-sm">Training Setup</h4>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="p-2 bg-[hsl(var(--secondary))] rounded-lg text-center">
                      <p className="text-violet-400 text-xs font-medium">Difficulty</p>
                      <p className="text-white text-sm font-bold">Mixed</p>
                    </div>
                    <div className="p-2 bg-[hsl(var(--secondary))] rounded-lg text-center">
                      <p className="text-violet-400 text-xs font-medium">Clues</p>
                      <p className="text-white text-sm font-bold">15</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {[5, 10, 15, 25].map((n) => (
                      <div key={n} className={`py-2 rounded-lg text-center text-xs font-bold ${n === 15 ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))]' : 'bg-[hsl(var(--secondary))] text-gray-400'}`}>
                        {n}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-violet-500/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl" />
            </div>

            {/* Right: Content */}
            <div className="order-1 lg:order-2">
              <Badge className="bg-violet-500/20 text-violet-400 border-violet-500/30 mb-4">
                <Sparkles className="w-3 h-3 mr-1" />
                Daily Categories
              </Badge>
              <h2 className="font-display font-bold text-3xl lg:text-4xl text-white mb-4">
                Fresh Categories.<br />
                <span className="text-[hsl(var(--brand-gold-500))]">Every Single Day.</span>
              </h2>
              <p className="text-gray-400 text-lg mb-6">
                Three hand-picked categories featured daily. Choose your difficulty, set your clue count, 
                and train at your own pace with AI-paraphrased clues.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">3 New Categories Daily</h4>
                    <p className="text-gray-500 text-sm">Always fresh, never repetitive</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Flexible Training</h4>
                    <p className="text-gray-500 text-sm">5, 10, 15, or 25 clues per session</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-violet-500/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Cross-Mode Stats</h4>
                    <p className="text-gray-500 text-sm">Compare performance across Sprint & Daily Categories</p>
                  </div>
                </div>
              </div>
              
              <Link to="/register">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                  Try Daily Categories
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== ASKALEX SHOWCASE ==================== */}
      <div className="py-20 bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 mb-4">
                <Brain className="w-3 h-3 mr-1" />
                AskAlex
              </Badge>
              <h2 className="font-display font-bold text-3xl lg:text-4xl text-white mb-4">
                Data-Driven Insights.<br />
                <span className="text-[hsl(var(--brand-gold-500))]">Train Like a Champion.</span>
              </h2>
              <p className="text-gray-400 text-lg mb-6">
                Unlock the power of data analysis. Complete missions, beat historical 
                contestant averages, and build your knowledge portfolio with AI-powered insights.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Target className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Data Missions</h4>
                    <p className="text-gray-500 text-sm">12 unique missions analyzing real Jeopardy! patterns</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Beat the Data</h4>
                    <p className="text-gray-500 text-sm">Compare your accuracy to real contestants</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <PieChart className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Knowledge Portfolio</h4>
                    <p className="text-gray-500 text-sm">Track your expertise across all categories</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Lightbulb className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Clue Explorer</h4>
                    <p className="text-gray-500 text-sm">Search 514K+ clues, discover patterns, and find practice gaps</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Award className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Earn Badges</h4>
                    <p className="text-gray-500 text-sm">Unlock achievements as you master the data</p>
                  </div>
                </div>
              </div>
              
              <Link to="/register">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                  Explore AskAlex
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            {/* Right: Screenshot Mockups */}
            <div className="relative space-y-4">
              {/* Data Missions Card */}
              <div className="bg-[hsl(var(--secondary))] rounded-2xl p-5 border border-[hsl(var(--border))] shadow-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-5 h-5 text-amber-400" />
                  <h4 className="text-white font-bold">Data Missions</h4>
                  <Badge className="ml-auto bg-amber-500/20 text-amber-400 text-xs">12 Available</Badge>
                </div>
                
                {/* Mission Cards Preview */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {[
                    { name: 'Category Champion', xp: 150, icon: '🏆', progress: 100 },
                    { name: 'Decade Dominator', xp: 200, icon: '📅', progress: 75 },
                    { name: 'Value Hunter', xp: 175, icon: '💰', progress: 50 },
                    { name: 'Difficulty Analyst', xp: 225, icon: '📊', progress: 0 },
                  ].map((mission, idx) => (
                    <div key={mission.name} className={`p-3 rounded-lg border ${mission.progress === 100 ? 'bg-green-500/10 border-green-500/30' : 'bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{mission.icon}</span>
                        <span className="text-white text-xs font-medium truncate">{mission.name}</span>
                      </div>
                      <div className="h-1.5 bg-[hsl(var(--secondary))] rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${mission.progress === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                          style={{ width: `${mission.progress}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-gray-500 text-xs">{mission.progress}%</span>
                        <span className="text-amber-400 text-xs">+{mission.xp} XP</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Beat the Data Preview */}
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-2xl p-5 border border-[hsl(var(--border))] shadow-xl">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="w-5 h-5 text-emerald-400" />
                  <h4 className="text-white font-bold">Beat the Data</h4>
                </div>
                
                <div className="bg-[hsl(var(--brand-blue-900))] rounded-xl p-4 mb-3">
                  <p className="text-amber-400 text-xs mb-2">SCIENCE</p>
                  <p className="text-white text-sm mb-3">This element, atomic number 79, has been prized for jewelry since ancient times...</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs">Contestant Accuracy:</span>
                      <span className="text-emerald-400 font-bold">72%</span>
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">Can you beat it?</Badge>
                  </div>
                </div>
                
                {/* Stats Preview */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-[hsl(var(--brand-blue-900))] rounded-lg">
                    <p className="text-emerald-400 font-bold">78%</p>
                    <p className="text-gray-500 text-xs">Your Avg</p>
                  </div>
                  <div className="text-center p-2 bg-[hsl(var(--brand-blue-900))] rounded-lg">
                    <p className="text-white font-bold">65%</p>
                    <p className="text-gray-500 text-xs">Beat Rate</p>
                  </div>
                  <div className="text-center p-2 bg-[hsl(var(--brand-blue-900))] rounded-lg">
                    <p className="text-amber-400 font-bold">24</p>
                    <p className="text-gray-500 text-xs">Sessions</p>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-orange-500/10 rounded-full blur-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* ==================== CREATE A BOARD SHOWCASE ==================== */}
      <div className="py-20 bg-[hsl(var(--background))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Board Grid Mockup */}
            <div className="relative order-2 lg:order-1">
              <div className="bg-[hsl(var(--brand-blue-800))] rounded-2xl p-5 border border-[hsl(var(--border))] shadow-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <LayoutGrid className="w-5 h-5 text-emerald-400" />
                  <h4 className="text-white font-bold">Your Custom Board</h4>
                  <Badge className="ml-auto bg-emerald-500/20 text-emerald-400 text-xs">Round 1</Badge>
                </div>

                {/* Mini Board Grid */}
                <div className="space-y-1.5 mb-4">
                  {/* Category Headers */}
                  <div className="grid grid-cols-5 gap-1.5">
                    {['SCIENCE', 'HISTORY', 'MUSIC', 'FOOD', 'SPORTS'].map((cat) => (
                      <div key={cat} className="bg-[hsl(var(--brand-blue-900))] rounded-md py-2 px-1 text-center">
                        <span className="text-emerald-400 text-[10px] font-bold tracking-wide">{cat}</span>
                      </div>
                    ))}
                  </div>
                  {/* Value Rows */}
                  {[100, 200, 300, 400, 500].map((val) => (
                    <div key={val} className="grid grid-cols-5 gap-1.5">
                      {[0, 1, 2, 3, 4].map((col) => {
                        const isRevealed = (val === 100 && col === 2) || (val === 300 && col === 0) || (val === 200 && col === 4);
                        const isActive = val === 200 && col === 1;
                        return (
                          <div
                            key={col}
                            className={`rounded-md py-2.5 text-center text-sm font-bold transition-all ${
                              isActive
                                ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] scale-105 shadow-lg shadow-[hsl(var(--brand-gold-500))]/30'
                                : isRevealed
                                ? 'bg-[hsl(var(--brand-blue-900))]/50 text-gray-600'
                                : 'bg-[hsl(var(--brand-blue-900))] text-[hsl(var(--brand-gold-500))]'
                            }`}
                          >
                            {isRevealed ? '' : `$${val}`}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* Round Indicators */}
                <div className="flex items-center justify-center gap-3 mb-4">
                  {['Round 1', 'Round 2', 'Final Round'].map((round, idx) => (
                    <div
                      key={round}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                        idx === 0
                          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                          : 'bg-[hsl(var(--brand-blue-900))] text-gray-500 border border-[hsl(var(--border))]'
                      }`}
                    >
                      {idx === 0 && <Play className="w-3 h-3" />}
                      {round}
                    </div>
                  ))}
                </div>

                {/* Beat the Score Preview */}
                <div className="bg-[hsl(var(--brand-blue-900))] rounded-lg p-3 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Crown className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                      <span className="text-gray-400 text-xs">Leader: <span className="text-white font-medium">Sarah M.</span></span>
                    </div>
                    <span className="text-[hsl(var(--brand-gold-500))] font-bold text-sm">$4,200</span>
                  </div>
                  <p className="text-emerald-400 text-[10px] text-center mt-1.5">Can you beat the score?</p>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-teal-500/10 rounded-full blur-2xl" />
            </div>

            {/* Right: Content */}
            <div className="order-1 lg:order-2">
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-4">
                <LayoutGrid className="w-3 h-3 mr-1" />
                Create a Board
              </Badge>
              <h2 className="font-display font-bold text-3xl lg:text-4xl text-white mb-4">
                Build Your Own Board.<br />
                <span className="text-[hsl(var(--brand-gold-500))]">Challenge the Community.</span>
              </h2>
              <p className="text-gray-400 text-lg mb-6">
                Generate a full game board with AI, play through 3 rounds of escalating value, 
                then share your score to the community and dare others to beat it.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">AI-Generated Clues</h4>
                    <p className="text-gray-500 text-sm">Custom boards created instantly across any topic</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Layers className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">3-Round Gameplay</h4>
                    <p className="text-gray-500 text-sm">Round 1, Round 2, and a Final Round wager</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Timed Clue Reveal</h4>
                    <p className="text-gray-500 text-sm">20-second timer per clue — skip with no penalty</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">Beat the Score</h4>
                    <p className="text-gray-500 text-sm">Share your board to the community — others get one shot to dethrone you</p>
                  </div>
                </div>
              </div>

              <Link to="/register">
                <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white">
                  Create a Board
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-20 bg-[hsl(var(--background))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display font-bold text-3xl text-center text-white mb-4">
            What Players Are <span className="text-[hsl(var(--brand-gold-500))]">Saying</span>
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Join thousands of trivia enthusiasts who are already training with JepPrep
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <Card key={testimonial.name} className="bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] card-hover">
                <CardContent className="p-6">
                  <Quote className="w-8 h-8 text-[hsl(var(--brand-gold-500))] mb-4 opacity-50" />
                  <p className="text-gray-300 mb-6 italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">{testimonial.name}</p>
                      <p className="text-gray-500 text-sm">{testimonial.title}</p>
                    </div>
                    {renderStars(testimonial.rating)}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Sample Jeopardy Questions Section */}
      <SampleQuestionsSection />

      {/* FAQ Section */}
      <FAQSection />

      {/* Blog Carousel */}
      <BlogCarousel />

      {/* CTA Section */}
      <div className="py-20 bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="font-display font-bold text-3xl text-white mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-gray-400 mb-8">
            Join thousands of trivia enthusiasts training to become Jeopardy! champions.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 py-6 text-lg font-semibold">
              Create Free Account
            </Button>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 bg-[hsl(var(--background))] border-t border-[hsl(var(--border))]">
        <div className="mx-auto max-w-7xl px-4 flex flex-col items-center gap-3">
          <img src={LOGO_URL} alt="Jeopardy practice" className="h-12" />
            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-gray-500 text-sm">
            <Link to="/pricing" className="hover:text-white transition-colors" data-testid="footer-pricing-link">Pricing</Link>
            <span className="hidden sm:inline">|</span>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</Link>
            <span className="hidden sm:inline">|</span>
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link>
            <span className="hidden sm:inline">|</span>
            <Link to="/blog" className="hover:text-white transition-colors" data-testid="footer-blog-link">Blog</Link>
            <span className="hidden sm:inline">|</span>
            <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact</Link>
          </div>
          <p className="text-gray-500 text-sm text-center max-w-2xl">
            © 2026 JepPrep. This application is an independent, fan-made trivia training tool and is not affiliated with, 
            endorsed by, or sponsored by Jeopardy!, Sony Pictures Television, or any of their subsidiaries or affiliates. 
            "Jeopardy!" is a registered trademark of Sony Pictures Television. All trademarks belong to their respective owners.
          </p>
        </div>
      </footer>
    </div>
  );
}
